import React from 'react';
import { useTranslation } from 'react-i18next';
import { BrandCombobox } from '@/components/BrandCombobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { brandsForType, sizePlaceholder } from '@/data/kiteBrands';
import { Wind, Layers } from 'lucide-react';

const Block = ({ icon: Icon, title, type, brand, size, saved, onChange, fromLabel, quickPick }) => {
  const { t } = useTranslation();
  const gearLabel = (g) => [g.brand, g.size, g.model].filter(Boolean).join(' ') || t('equipmentPicker.unnamed');
  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-3.5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-[#48CAE4]" />
        <span className="text-sm font-medium">{title}</span>
      </div>

      {saved.length > 0 && (
        <div className="mb-3">
          <Label className="text-xs text-muted-foreground">{fromLabel}</Label>
          <Select
            value={brand || size ? '__manual__' : ''}
            onValueChange={(v) => {
              if (v === '__manual__') return;
              const g = saved[Number(v)];
              if (g) onChange({ brand: g.brand || '', size: g.size || '' });
            }}>
            <SelectTrigger className="mt-1 bg-[#12161B] border-white/15" data-testid={`equip-${type}-saved`}>
              <SelectValue placeholder={quickPick} />
            </SelectTrigger>
            <SelectContent className="bg-[#12161B] border-white/15 text-white">
              {saved.map((g, i) => <SelectItem key={i} value={String(i)}>{gearLabel(g)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-5 gap-2">
        <div className="col-span-3">
          <Label className="text-xs text-muted-foreground">{t('equipmentPicker.brand')}</Label>
          <div className="mt-1">
            <BrandCombobox value={brand} onChange={(v) => onChange({ brand: v, size })}
              options={brandsForType(type)} placeholder={t('equipmentPicker.brandPlaceholder')} testid={`equip-${type}-brand`} />
          </div>
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-muted-foreground">{t('equipmentPicker.size')}</Label>
          <Input value={size} onChange={(e) => onChange({ brand, size: e.target.value })}
            placeholder={sizePlaceholder(type)} className="mt-1 bg-[#12161B] border-white/15" data-testid={`equip-${type}-size`} />
        </div>
      </div>
    </div>
  );
};

// value: { kite:{brand,size}, board:{brand,size} }
export const EquipmentPicker = ({ savedGear = [], value, onChange }) => {
  const { t } = useTranslation();
  const kites = savedGear.filter((g) => g.type === 'kite');
  const boards = savedGear.filter((g) => g.type === 'board');

  return (
    <div className="space-y-3" data-testid="equipment-picker">
      <Block icon={Wind} title={t('equipmentPicker.kite')} type="kite" brand={value.kite.brand} size={value.kite.size}
        saved={kites} onChange={(patch) => onChange({ ...value, kite: { ...value.kite, ...patch } })}
        fromLabel={t('equipmentPicker.fromMyKites')} quickPick={t('equipmentPicker.quickPickKite')} />
      <Block icon={Layers} title={t('equipmentPicker.board')} type="board" brand={value.board.brand} size={value.board.size}
        saved={boards} onChange={(patch) => onChange({ ...value, board: { ...value.board, ...patch } })}
        fromLabel={t('equipmentPicker.fromMyBoards')} quickPick={t('equipmentPicker.quickPickBoard')} />
    </div>
  );
};

// build a readable equipment string from the picker value
export const composeEquipment = (value) => {
  const parts = [];
  const k = `${value.kite.brand} ${value.kite.size}`.trim();
  const b = `${value.board.brand} ${value.board.size}`.trim();
  if (k) parts.push(`Kite: ${k}`);
  if (b) parts.push(`Board: ${b}`);
  return parts.join(' \u00b7 ');
};
